/**
 * How a block's calendar commitment looks and reads on the calendar
 * (constitution ratified decision #2 + SC-36).
 *
 * Copy and styling only — the vocabulary itself lives in
 * services/scheduleService. Users see approximately Flexible / Confirmed;
 * `commitment_source`, `block_status` and the rest of the internal model
 * never surface here.
 *
 * A block whose commitment is unknown (the payload carried none) gets the
 * neutral treatment — solid border, no glyph, no label. Silence is not
 * "Confirmed": claiming a commitment the user never made is exactly the
 * dishonesty this surface exists to remove.
 */

import type { CommitmentState } from '../../services/scheduleService'
import { PRODUCT_NAME } from './productName'

export const COMMITMENT_LABEL: Record<CommitmentState, string> = {
  flexible: 'Flexible',
  confirmed: 'Confirmed',
}

/**
 * Plain-language explanation, for tooltips and screen readers.
 *
 * Ratified decision #2 fixes the two nouns: **Flexible** and
 * **Confirmed**. "Pinned" and "Locked" are rejected terms — the first
 * collides with the live attention-plane pin, the second over-implies
 * immutability and translates poorly.
 */
export const COMMITMENT_HINT: Record<CommitmentState, string> = {
  flexible: `Flexible — ${PRODUCT_NAME} picked this time and can still move it.`,
  confirmed: `Confirmed — your time. ${PRODUCT_NAME} leaves it where it is.`,
}

/** Left-border style: dashed reads soft, solid reads committed. */
export function commitmentBorder(
  state: CommitmentState | null | undefined,
): 'solid' | 'dashed' {
  return state === 'flexible' ? 'dashed' : 'solid'
}

/**
 * Alpha suffix for the pill fill. Flexible sits lighter so a calendar of
 * holds reads as a sketch, not as a wall of obligations.
 */
export function commitmentFillAlpha(
  state: CommitmentState | null | undefined,
): string {
  return state === 'flexible' ? '1f' : '30'
}
